/**
 * 画像の余白部分をトリミングする関数
 * @param image HTMLImageElement オブジェクト
 * @param outputFormat 出力する画像形式（例："image/png", "image/webp"）
 * @param quality 圧縮品質（0〜1）
 * @returns Promise<Blob> トリミングされた画像のBlob
 */
export const trimImage = (
  image: HTMLImageElement,
  outputFormat: string = "image/png",
  quality: number = 1.0
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Canvasコンテキストにアクセスできません"));
      return;
    }

    // 元画像をキャンバスに描画
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    // ピクセルデータを取得
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data, width, height } = imageData;

    // トリミング領域を計算
    let top = 0, left = 0, right = width, bottom = height;

    // 上端の余白を探す
    for (let y = 0; y < height; y++) {
      if (Array.from({ length: width }).some((_, x) => data[(y * width + x) * 4 + 3] > 0)) {
        top = y;
        break;
      }
    }

    // 下端の余白を探す
    for (let y = height - 1; y >= 0; y--) {
      if (Array.from({ length: width }).some((_, x) => data[(y * width + x) * 4 + 3] > 0)) {
        bottom = y + 1;
        break;
      }
    }

    // 左端の余白を探す
    for (let x = 0; x < width; x++) {
      if (Array.from({ length: height }).some((_, y) => data[(y * width + x) * 4 + 3] > 0)) {
        left = x;
        break;
      }
    }

    // 右端の余白を探す
    for (let x = width - 1; x >= 0; x--) {
      if (Array.from({ length: height }).some((_, y) => data[(y * width + x) * 4 + 3] > 0)) {
        right = x + 1;
        break;
      }
    }

    const newWidth = right - left;
    const newHeight = bottom - top;

    // トリミング後の幅と高さが有効か確認
    if (newWidth <= 0 || newHeight <= 0) {
      // 有効なトリミング領域が見つからなかった場合、画像全体を使用
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);
    } else {
      // トリミング領域を設定
      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(image, left, top, newWidth, newHeight, 0, 0, newWidth, newHeight);
    }

    // Blob形式で返す
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("トリミングされたCanvasからBlobを作成できませんでした"));
        }
      },
      outputFormat,
      quality // 圧縮品質
    );
  });
};

/**
 * 画像を指定された最大高さまでリサイズする関数
 * @param image HTMLImageElement オブジェクト
 * @param maxHeight 最大高さ（ピクセル）
 * @param outputFormat 出力する画像形式（例："image/png", "image/webp"）
 * @param quality 圧縮品質（0〜1）
 * @returns Promise<Blob> リサイズされた画像のBlob
 */
export const resizeImage = (
  image: HTMLImageElement,
  maxHeight: number,
  outputFormat: string = "image/webp",
  quality: number = 0.8
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    let { width, height } = image;

    if (height <= maxHeight) {
      // 縮小不要の場合は既存の画像を返す
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvasコンテキストにアクセスできません"));
        return;
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(image, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("画像のBlob化に失敗しました"));
          }
        },
        outputFormat,
        quality
      );
      return;
    }

    // 新しい寸法を計算
    const aspectRatio = width / height;
    const newHeight = maxHeight;
    const newWidth = newHeight * aspectRatio;

    // リサイズ用Canvasを作成
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Canvasコンテキストにアクセスできません"));
      return;
    }

    canvas.width = newWidth;
    canvas.height = newHeight;
    ctx.drawImage(image, 0, 0, newWidth, newHeight);

    // Blob形式で返す
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("リサイズされたCanvasからBlobを作成できませんでした"));
        }
      },
      outputFormat,
      quality // 圧縮品質
    );
  });
};

/**
 * 画像を正方形にトリミングする関数
 * @param image HTMLImageElement オブジェクト
 * @param outputFormat 出力する画像形式（例："image/webp"）
 * @param quality 圧縮品質（0〜1）
 * @returns Promise<Blob> トリミングされた正方形画像のBlob
 */
export const cropToSquare = (
  image: HTMLImageElement,
  outputFormat: string = "image/webp",
  quality: number = 0.8
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const sideLength = Math.min(image.width, image.height);
    const offsetX = (image.width - sideLength) / 2;
    const offsetY = (image.height - sideLength) / 2;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Canvasコンテキストにアクセスできません"));
      return;
    }

    canvas.width = sideLength;
    canvas.height = sideLength;
    ctx.drawImage(image, offsetX, offsetY, sideLength, sideLength, 0, 0, sideLength, sideLength);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("正方形にトリミングされたCanvasからBlobを作成できませんでした"));
        }
      },
      outputFormat,
      quality
    );
  });
}; 